import { all, call, put, select } from 'redux-saga/effects'

import { updateFlagOpenings } from '../../../api/projects-api'
import { FlagFilters, FlagOpeningLinksAPIUpdatePayload, NormalizedFlag } from '../../../models/flags'
import { DRAWING_TYPES, PAPER_ELEMENT_TYPE } from '../../../shared/constants/drawable-types'
import { isFlagVisibleBasedOnFilters } from '../../../shared/services/flags/flags-service'
import managers from '../../lib/managers'
import { Flag, Workspace } from '../../lib/toolBoxes/2D'
import addTooltipsToFlag from '../../lib/utils/functionality-bindings/addTooltipsToFlag'
import {
    selectFlagFilters,
    selectMaterialFlagIndexByOpeningId,
    selectMaterialFlags,
    updateFlagOpeningCoordinates,
} from '../../slices/materialFlags'
import { selectVisibility } from '../../slices/tools'
import { IMUP2DCoordinatesToUpdate, Vector2D } from '../../types'

export function* redrawFlagsOnUpdate(openingId: number, coordinates: IMUP2DCoordinatesToUpdate | null) {
    if (coordinates === null) {
        console.error('Could not redraw flags on update, coordinates are missing')

        return
    }

    try {
        const paperManager = yield call(managers.get2DManager)
        const workspaceTool: Workspace = yield call(paperManager.getTool, Workspace.NAME)
        const flagTool: Flag = yield call(paperManager.getTool, Flag.NAME)

        const flags: NormalizedFlag[] = yield select(selectMaterialFlags)
        const materialFlagIndexByOpeningId: ReturnType<typeof selectMaterialFlagIndexByOpeningId> = yield select(
            selectMaterialFlagIndexByOpeningId
        )

        // A highlight is always an area shape (it never sets shapeType), so treat it the same as a material area
        const drawableAreaItems: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) =>
                data.drawable_id === openingId &&
                (data.shapeType === DRAWING_TYPES.AREA || data.type === PAPER_ELEMENT_TYPE.HIGHLIGHT)
        )

        // prepare data to be sent to backend
        const preparedUpdateOpeningLinkData: FlagOpeningLinksAPIUpdatePayload[] = []

        flags.forEach((flag) => {
            flag.opening_links.forEach((link) => {
                if (link.opening_id === openingId) {
                    // TODO: instead center coordinates get the coordinates on the material
                    const drawableCenterCoordinates = flagTool.getFlagPointFromCoordinates(
                        coordinates.coordinates,
                        !!drawableAreaItems.length,
                        materialFlagIndexByOpeningId[openingId]?.[flag.id]
                    )

                    preparedUpdateOpeningLinkData.push({
                        id: link.id,
                        flag_id: flag.id,
                        coordinates: drawableCenterCoordinates,
                    })
                }
            })
        })

        // get flag ids to remove them from blueprint
        const flagIdsMapToRemove: Map<number, FlagOpeningLinksAPIUpdatePayload> = yield new Map(
            preparedUpdateOpeningLinkData.map((flag) => {
                return [flag.flag_id, flag]
            })
        )

        if (!flagIdsMapToRemove.size) return

        // get flags to remove
        const temporaryFlagItems: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) => flagIdsMapToRemove.has(data?.flag_id) && data?.openingId === openingId
        )

        // remove flags
        yield all(temporaryFlagItems.map((item) => call(item.remove.bind(item))))

        // api call to update coordinates for opening_links
        yield call(updateFlagOpenings, { opening_links: preparedUpdateOpeningLinkData })

        // update coordinates in flag opening_links
        yield put(updateFlagOpeningCoordinates({ newOpeningLinkData: preparedUpdateOpeningLinkData }))

        const flagsToRedraw = flags.reduce<
            {
                flag_id: number
                order: number
                type: string | null
                status: number | null
                jiraStatusId: number | null
                coordinates: Vector2D
                linkId: number
            }[]
        >((acc, flag) => {
            const updatedFlag = flagIdsMapToRemove.get(flag.id)

            if (updatedFlag) {
                acc.push({
                    flag_id: flag.id,
                    order: flag.order,
                    type: flag.type?.name || null,
                    status: flag.status?.id || null,
                    jiraStatusId: flag?.creationJiraStatus?.id || null,
                    coordinates: updatedFlag.coordinates,
                    linkId: updatedFlag.id,
                })
            }

            return acc
        }, [])

        const flagFilters: FlagFilters = yield select(selectFlagFilters)
        const { isMaterialFlagsVisible } = yield select(selectVisibility)

        // re-draw flags based on updated material coordinates
        const redrawnFlags = yield all(
            flagsToRedraw.map((link) =>
                call(
                    flagTool.drawFlagByCoords,
                    link.coordinates,
                    link.flag_id,
                    openingId,
                    link.order,
                    link.type,
                    isMaterialFlagsVisible &&
                        isFlagVisibleBasedOnFilters(flagFilters, link.type, link.status, link.jiraStatusId),
                    link.status,
                    link.jiraStatusId,
                    false,
                    link.linkId
                )
            )
        )

        // Reattach tooltips and event handlers to redrawn flags
        yield all(redrawnFlags.map((flag) => call(addTooltipsToFlag, flag, workspaceTool)))
    } catch (error) {
        console.error(error)
    }
}
